import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TablePagination,
  LinearProgress,
  Alert,
  Chip,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Tooltip,
} from '@mui/material';
import {
  MdSearch,
  MdRefresh,
  MdInfoOutline,
  MdFileDownload,
  MdGridOn,
  MdPrint,
  MdStorefront,
  MdCurrencyRupee,
  MdPercent,
  MdAccountBalanceWallet,
  MdPayments,
  MdTaskAlt,
  MdEmojiEvents,
} from 'react-icons/md';
import { getData } from '../../Webservice/Webservice';
import {
  FONT,
  inr,
  fmtDate,
  toISO,
  authToken,
  PRESETS,
  presetRange,
  rangeLabel,
  StatCard,
  statGridSx,
  exportButtonSx,
  downloadCsv,
  downloadExcel,
  openPrintView,
} from './reportShared';

const REPORT_ROUTE = 'reports/seller-sales';
const pill = (bg, fg) => ({ fontWeight: 700, fontFamily: FONT, background: bg, color: fg, height: 24, fontSize: 11 });
const label = (s) => (s ? String(s).replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, (c) => c.toUpperCase()) : '-');
const dash = <Typography component="span" sx={{ color: '#94a3b8' }}>—</Typography>;

const SELLER_STATUS = { approved: ['#dcfce7', '#166534'], pending: ['#fef3c7', '#92400e'], waitingForApproval: ['#e0f2fe', '#075985'], rejected: ['#fee2e2', '#991b1b'], deleted: ['#f1f5f9', '#64748b'] };
const REQ_STATUS = { pending: ['#fef3c7', '#92400e'], accepted: ['#e0e7ff', '#3730a3'], packed: ['#cffafe', '#155e75'], shipped: ['#ede9fe', '#5b21b6'], delivered: ['#dcfce7', '#166534'], rejected: ['#fee2e2', '#991b1b'] };
const PAYOUT_STATUS = { initiated: ['#fef3c7', '#92400e'], processing: ['#e0f2fe', '#075985'], completed: ['#dcfce7', '#166534'], failed: ['#fee2e2', '#991b1b'] };
const chip = (map, v) => {
  const [bg, fg] = map[v] || ['#f1f5f9', '#475569'];
  return <Chip size="small" label={label(v)} sx={pill(bg, fg)} />;
};
const rateChip = (v, good) => {
  if (v === null || v === undefined) return dash;
  const ok = good ? v >= 90 : v <= 5;
  const warn = good ? v >= 75 : v <= 15;
  const [bg, fg] = ok ? ['#dcfce7', '#166534'] : warn ? ['#fef3c7', '#92400e'] : ['#fee2e2', '#b91c1c'];
  return <Chip size="small" label={`${v}%`} sx={pill(bg, fg)} />;
};
const signedMoney = (v) => <span style={{ color: v < 0 ? '#b91c1c' : undefined }}>{inr(v)}</span>;

const SellerCell = ({ r }) => (
  <Box sx={{ lineHeight: 1.25, minWidth: 170 }}>
    <Typography sx={{ fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>{r.seller}</Typography>
    <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{[r.sellerCode, r.contact].filter(Boolean).join(' · ') || '—'}</Typography>
  </Box>
);

const col = (key, lbl, opts = {}) => ({ key, label: lbl, ...opts });
const money = (key, lbl, extra = {}) => col(key, lbl, { numeric: true, text: (r) => r[key], cell: (r) => signedMoney(r[key]), print: (r) => inr(r[key]), ...extra });
const sellerCol = col('seller', 'Seller', { text: (r) => r.seller, cell: (r) => <SellerCell r={r} />, extra: (r) => ({ 'Seller Code': r.sellerCode }) });

const TABS = [
  {
    key: 'sellers',
    label: 'By Seller',
    defaultSort: 'gross_desc',
    sorts: [
      ['gross_desc', 'Gross sales: high to low'],
      ['commission_desc', 'Commission: high to low'],
      ['balance_desc', 'Balance due: high to low'],
      ['fulfil_asc', 'Fulfilment: lowest first'],
      ['reject_desc', 'Rejection: highest first'],
      ['name_asc', 'Name: A to Z'],
    ],
    columns: [
      col('rank', '#', { center: true, text: (r, i) => i, skipExport: true }),
      col('seller', 'Seller', { text: (r) => r.seller, cell: (r) => <SellerCell r={r} />, extra: (r) => ({ 'Seller Code': r.sellerCode, Contact: r.contact }) }),
      col('status', 'Status', { text: (r) => label(r.status), cell: (r) => chip(SELLER_STATUS, r.status) }),
      col('products', 'Products', { numeric: true, text: (r) => r.products }),
      col('orders', 'Orders', { numeric: true, text: (r) => r.orders }),
      money('grossSales', 'Gross Sales', { bold: true }),
      money('returns', 'Returns'),
      money('netSales', 'Net Sales', { bold: true }),
      col('commission', 'Commission', {
        numeric: true,
        text: (r) => r.commission,
        extra: (r) => ({ 'Commission Rate %': r.commissionPct, 'Effective %': r.effectiveCommissionPct ?? '' }),
        cell: (r) => (
          <Box sx={{ lineHeight: 1.25 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#15803d', fontFamily: FONT }}>{inr(r.commission)}</Typography>
            <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>
              {r.commissionPct ?? '—'}% rate{r.effectiveCommissionPct !== null && r.effectiveCommissionPct !== r.commissionPct ? ` · ${r.effectiveCommissionPct}% eff.` : ''}
            </Typography>
          </Box>
        ),
        print: (r) => `${inr(r.commission)} (${r.commissionPct ?? '-'}%)`,
      }),
      money('netEarnings', 'Seller Earnings'),
      money('paidOut', 'Paid Out'),
      col('balanceDue', 'Balance Due', {
        numeric: true,
        bold: true,
        text: (r) => r.balanceDue,
        print: (r) => inr(r.balanceDue),
        cell: (r) => <span style={{ color: r.balanceDue > 0 ? '#b45309' : '#334155' }}>{inr(r.balanceDue)}</span>,
      }),
      col('fulfilmentRate', 'Fulfilment', { center: true, text: (r) => (r.fulfilmentRate === null ? '' : `${r.fulfilmentRate}%`), cell: (r) => rateChip(r.fulfilmentRate, true) }),
      col('rejectionRate', 'Rejection', { center: true, text: (r) => (r.rejectionRate === null ? '' : `${r.rejectionRate}%`), cell: (r) => rateChip(r.rejectionRate, false) }),
      col('pendingRequests', 'Pending Req.', { numeric: true, text: (r) => r.pendingRequests, cell: (r) => (r.pendingRequests ? <Chip size="small" label={r.pendingRequests} sx={pill('#fef3c7', '#92400e')} /> : 0) }),
      col('avgAcceptHours', 'Avg Accept Time', { center: true, text: (r) => (r.avgAcceptHours === null ? '' : `${r.avgAcceptHours}h`), cell: (r) => (r.avgAcceptHours === null ? dash : `${r.avgAcceptHours} h`) }),
    ],
  },
  {
    key: 'transactions',
    label: 'Transactions',
    defaultSort: 'date_desc',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
      ['amount_desc', 'Amount: high to low'],
    ],
    columns: [
      col('date', 'Date', { text: (r) => fmtDate(r.date) }),
      sellerCol,
      col('orderNumber', 'Order No.', { bold: true, text: (r) => r.orderNumber }),
      col('type', 'Type', { text: (r) => label(r.type), cell: (r) => <Chip size="small" label={r.type === 'return' ? 'Return' : 'Sale'} sx={r.type === 'return' ? pill('#ffedd5', '#9a3412') : pill('#e0e7ff', '#3730a3')} /> }),
      money('grossAmount', 'Gross'),
      col('commissionPct', 'Rate', { numeric: true, text: (r) => `${r.commissionPct}%` }),
      money('commission', 'Commission'),
      money('netAmount', 'Seller Net', { bold: true }),
      col('status', 'Settlement', { text: (r) => label(r.status), cell: (r) => <Chip size="small" label={label(r.status)} sx={r.status === 'settled' ? pill('#dcfce7', '#166534') : pill('#fef3c7', '#92400e')} /> }),
    ],
  },
  {
    key: 'fulfilment',
    label: 'Order Requests',
    defaultSort: 'date_desc',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
    ],
    columns: [
      col('date', 'Requested', { text: (r) => fmtDate(r.date) }),
      col('receiptId', 'Request No.', { bold: true, text: (r) => r.receiptId }),
      sellerCol,
      col('orderNumber', 'Order No.', { text: (r) => r.orderNumber }),
      col('product', 'Product', { text: (r) => r.product, extra: (r) => ({ 'Product Code': r.productCode }) }),
      col('quantity', 'Qty', { center: true, text: (r) => r.quantity }),
      col('status', 'Status', { text: (r) => label(r.status), cell: (r) => chip(REQ_STATUS, r.status) }),
      col('acceptHours', 'Accepted In', { center: true, text: (r) => (r.acceptHours === null ? '' : `${r.acceptHours}h`), cell: (r) => (r.acceptHours === null ? dash : `${r.acceptHours} h`) }),
      col('estdArrivalDate', 'Est. Arrival', { text: (r) => (r.estdArrivalDate ? fmtDate(r.estdArrivalDate) : '') }),
      col('rejectionReason', 'Rejection Reason', {
        text: (r) => r.rejectionReason,
        cell: (r) =>
          r.rejectionReason ? (
            <Tooltip title={r.rejectionReason} arrow>
              <Typography sx={{ fontSize: 11.5, color: '#b91c1c', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', fontFamily: FONT }}>{r.rejectionReason}</Typography>
            </Tooltip>
          ) : (
            dash
          ),
      }),
    ],
  },
  {
    key: 'payouts',
    label: 'Payouts',
    defaultSort: 'date_desc',
    sorts: [
      ['date_desc', 'Newest first'],
      ['date_asc', 'Oldest first'],
      ['amount_desc', 'Amount: high to low'],
    ],
    columns: [
      col('date', 'Initiated', { text: (r) => fmtDate(r.date) }),
      sellerCol,
      money('amount', 'Amount', { bold: true }),
      col('paymentMethod', 'Method', { text: (r) => label(r.paymentMethod) }),
      col('gateway', 'Gateway', { text: (r) => label(r.gateway) }),
      col('reference', 'Reference', { text: (r) => r.reference }),
      col('status', 'Status', { text: (r) => label(r.status), cell: (r) => chip(PAYOUT_STATUS, r.status) }),
      col('settledAt', 'Settled On', { text: (r) => (r.settledAt ? fmtDate(r.settledAt) : '') }),
    ],
  },
];

const SellerSalesReport = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const tab = TABS[tabIndex];
  const [preset, setPreset] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [seller, setSeller] = useState('');
  const [status, setStatus] = useState('');
  const [txType, setTxType] = useState('');
  const [settle, setSettle] = useState('');
  const [reqStatus, setReqStatus] = useState('');
  const [payoutStatus, setPayoutStatus] = useState('');
  const [sortBy, setSortBy] = useState(TABS[0].defaultSort);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  const filterParams = useMemo(() => {
    const p = { tab: tab.key, sortBy };
    if (from) p.from = from;
    if (to) p.to = to;
    if (search) p.search = search;
    if (seller) p.seller = seller;
    if (status) p.status = status;
    if (tab.key === 'transactions') {
      if (txType) p.type = txType;
      if (settle) p.settle = settle;
    }
    if (tab.key === 'fulfilment' && reqStatus) p.reqStatus = reqStatus;
    if (tab.key === 'payouts' && payoutStatus) p.payoutStatus = payoutStatus;
    return p;
  }, [tab.key, sortBy, from, to, search, seller, status, txType, settle, reqStatus, payoutStatus]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, page: page + 1, limit: rowsPerPage }, authToken(), false);
      if (res?.success) setData(res);
      else setError(res?.message || 'Failed to load report');
    } catch (err) {
      setError(err.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filterParams, page, rowsPerPage]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const goToTab = (i, after) => {
    setTabIndex(i);
    setSortBy(TABS[i].defaultSort);
    setPage(0);
    if (after) after();
  };
  const applyPreset = (key) => {
    const range = presetRange(key);
    setPreset(key);
    setFrom(range.from);
    setTo(range.to);
    setPage(0);
  };
  const onDateChange = (setter) => (e) => {
    setter(e.target.value);
    setPreset('');
    setPage(0);
  };
  const setFilter = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };
  const resetFilters = () => {
    setPreset('all');
    setFrom('');
    setTo('');
    setSearchInput('');
    setSearch('');
    setSeller('');
    setStatus('');
    setTxType('');
    setSettle('');
    setReqStatus('');
    setPayoutStatus('');
    setSortBy(tab.defaultSort);
    setPage(0);
  };

  const exportColumns = tab.columns.filter((c) => !c.skipExport);
  const exportObjects = (rows) =>
    rows.map((r) => {
      const obj = {};
      for (const c of exportColumns) {
        obj[c.label] = c.text(r);
        if (c.extra) Object.assign(obj, c.extra(r));
      }
      return obj;
    });
  const fileStamp = () => `Seller_${tab.label.replace(/[^A-Za-z]+/g, '_')}_${from || 'all'}_${to || toISO(new Date())}`;

  const runExport = async (kind) => {
    setExporting(true);
    try {
      const res = await getData(REPORT_ROUTE, { ...filterParams, export: 'all' }, authToken(), false);
      if (!res?.success) throw new Error(res?.message || 'Export failed');
      const rows = res.rows || [];
      if (kind === 'csv') downloadCsv(exportObjects(rows), fileStamp());
      else if (kind === 'excel') downloadExcel(exportObjects(rows), fileStamp(), tab.label);
      else
        openPrintView({
          title: `Seller Sales & Commission — ${tab.label}`,
          subtitle: `${rangeLabel(from, to)} · ${rows.length} rows`,
          headers: exportColumns.map((c) => c.label),
          cells: rows.map((r) => exportColumns.map((c) => ({ value: c.print ? c.print(r) : c.text(r), className: c.numeric ? 'num' : '' }))),
        });
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const stats = data?.stats || {};
  const total = data?.meta?.total || 0;
  const rows = data?.tab === tab.key ? data.rows || [] : [];
  const sellerOptions = data?.filterOptions?.sellers || [];
  const baseIndex = page * rowsPerPage;

  const cards = [
    { label: 'Active Sellers', value: (stats.activeSellers || 0).toLocaleString('en-IN'), icon: MdStorefront, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${stats.sellersWithSales || 0} made sales` },
    { label: 'Net Seller Sales', value: inr(stats.netSales), icon: MdCurrencyRupee, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: `Gross ${inr(stats.grossSales)} · returns ${inr(stats.returns)}` },
    { label: 'Commission Earned', value: inr(stats.commission), icon: MdPercent, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: stats.effectiveCommissionPct === null || stats.effectiveCommissionPct === undefined ? 'Net of returns' : `${stats.effectiveCommissionPct}% effective rate` },
    { label: 'Balance Due to Sellers', value: inr(stats.balanceDue), icon: MdAccountBalanceWallet, gradient: 'linear-gradient(135deg,#f59e0b,#ea580c)', sub: 'Unsettled, all time', onClick: () => goToTab(1, () => setSettle('pending')), active: tab.key === 'transactions' && settle === 'pending' },
    { label: 'Paid Out', value: inr(stats.paidOut), icon: MdPayments, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Completed payouts', onClick: () => goToTab(3, () => setPayoutStatus('completed')), active: tab.key === 'payouts' && payoutStatus === 'completed' },
    { label: 'Fulfilment Rate', value: stats.fulfilmentRate === null || stats.fulfilmentRate === undefined ? '—' : `${stats.fulfilmentRate}%`, icon: MdTaskAlt, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${stats.rejected || 0} rejected · ${stats.pendingRequests || 0} pending`, onClick: () => goToTab(2, () => setReqStatus('rejected')), active: tab.key === 'fulfilment' && reqStatus === 'rejected' },
    { label: 'Top Seller', value: stats.topSeller?.name || '—', icon: MdEmojiEvents, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: stats.topSeller ? `Net sales ${inr(stats.topSeller.netSales)}` : 'No sales' },
  ];

  const activeFilterCount = [from || to, search, seller, status, txType, settle, reqStatus, payoutStatus, sortBy !== tab.defaultSort].filter(Boolean).length;

  return (
    <Box sx={{ p: { xs: 1, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box>
          <Typography sx={{ fontSize: 23.5, fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>Seller Sales &amp; Commission Report</Typography>
          <Typography sx={{ fontSize: 12.5, color: '#64748b', fontFamily: FONT }}>Marketplace seller sales, your commission, what you owe them and how well they fulfil orders</Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant="outlined" startIcon={<MdInfoOutline />} onClick={() => setNotesOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '10px', fontFamily: FONT }}>Note</Button>
          <Button startIcon={<MdFileDownload />} disabled={exporting || !total} onClick={() => runExport('csv')} sx={exportButtonSx('#16a34a', '#15803d')}>CSV</Button>
          <Button startIcon={<MdGridOn />} disabled={exporting || !total} onClick={() => runExport('excel')} sx={exportButtonSx('#0d9488', '#0f766e')}>Excel</Button>
          <Button startIcon={<MdPrint />} disabled={exporting || !total} onClick={() => runExport('print')} sx={exportButtonSx('#7c3aed', '#6d28d9')}>Print / PDF</Button>
        </Box>
      </Box>

      <Box sx={statGridSx}>
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: '16px', mb: 3, border: '1px solid #e2e8f0' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>
            Filters {activeFilterCount > 0 && <Chip size="small" label={`${activeFilterCount} active`} color="primary" sx={{ ml: 1, fontWeight: 700 }} />}
          </Typography>
          <Button size="small" startIcon={<MdRefresh />} onClick={resetFilters} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Reset</Button>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              onClick={() => applyPreset(p.key)}
              sx={{
                fontWeight: 700,
                fontFamily: FONT,
                background: preset === p.key ? 'rgb(255 209 2)' : '#f1f5f9',
                color: '#0f172a',
                border: preset === p.key ? '1px solid #0f172a' : '1px solid transparent',
                '&:hover': { background: preset === p.key ? 'rgb(255 209 2)' : '#e2e8f0' },
              }}
            />
          ))}
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="From" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={from} onChange={onDateChange(setFrom)} inputProps={{ max: to || undefined }} />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField label="To" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={to} onChange={onDateChange(setTo)} inputProps={{ min: from || undefined }} />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={3}>
            <TextField
              label="Search seller name / code / phone"
              size="small"
              fullWidth
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              InputProps={{ startAdornment: (<InputAdornment position="start"><MdSearch size={18} /></InputAdornment>) }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Seller" size="small" fullWidth value={seller} onChange={setFilter(setSeller)} SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}>
              <MenuItem value="">All sellers</MenuItem>
              {sellerOptions.map((s) => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Seller status" size="small" fullWidth value={status} onChange={setFilter(setStatus)}>
              <MenuItem value="">All</MenuItem>
              {['approved', 'pending', 'waitingForApproval', 'rejected'].map((s) => (
                <MenuItem key={s} value={s}>{label(s)}</MenuItem>
              ))}
            </TextField>
          </Grid>
          {tab.key === 'transactions' && (
            <>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Type" size="small" fullWidth value={txType} onChange={setFilter(setTxType)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="order">Sales</MenuItem>
                  <MenuItem value="return">Returns</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={3} lg={2}>
                <TextField select label="Settlement" size="small" fullWidth value={settle} onChange={setFilter(setSettle)}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="settled">Settled</MenuItem>
                </TextField>
              </Grid>
            </>
          )}
          {tab.key === 'fulfilment' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Request status" size="small" fullWidth value={reqStatus} onChange={setFilter(setReqStatus)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="done">Accepted / fulfilled</MenuItem>
                {Object.keys(REQ_STATUS).map((s) => (
                  <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          {tab.key === 'payouts' && (
            <Grid item xs={12} sm={6} md={3} lg={2.5}>
              <TextField select label="Payout status" size="small" fullWidth value={payoutStatus} onChange={setFilter(setPayoutStatus)}>
                <MenuItem value="">All</MenuItem>
                {Object.keys(PAYOUT_STATUS).map((s) => (
                  <MenuItem key={s} value={s}>{label(s)}</MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3} lg={2.5}>
            <TextField select label="Sort by" size="small" fullWidth value={sortBy} onChange={setFilter(setSortBy)}>
              {tab.sorts.map(([v, l]) => (
                <MenuItem key={v} value={v}>{l}</MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        <Tabs
          value={tabIndex}
          onChange={(_, v) => goToTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderBottom: '1px solid #e2e8f0',
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontFamily: FONT, minHeight: 56 },
            '& .Mui-selected': { color: '#0f172a !important' },
            '& .MuiTabs-indicator': { background: 'rgb(255 209 2)', height: 4, borderRadius: 2 },
          }}
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box sx={{ px: 2.5, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <Typography sx={{ fontWeight: 800, fontFamily: FONT, color: '#0f172a' }}>{tab.label}</Typography>
          <Typography sx={{ fontSize: 11.5, color: '#64748b', fontFamily: FONT }}>{total.toLocaleString('en-IN')} rows</Typography>
        </Box>
        {(loading || exporting) && <LinearProgress />}
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                {tab.columns.map((c) => (
                  <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontWeight: 800, fontFamily: FONT, background: '#0f172a', color: '#fff', whiteSpace: 'nowrap', py: 1.5 }}>
                    {c.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tab.columns.length} sx={{ textAlign: 'center', color: '#94a3b8', py: 6, fontFamily: FONT }}>Nothing matches the selected filters.</TableCell>
                </TableRow>
              )}
              {rows.map((r, idx) => (
                <TableRow key={idx} hover sx={{ '&:nth-of-type(even)': { background: '#f8fafc' } }}>
                  {tab.columns.map((c) => (
                    <TableCell key={c.key} align={c.center ? 'center' : c.numeric ? 'right' : 'left'} sx={{ fontFamily: FONT, whiteSpace: 'nowrap', fontSize: 11.5, fontWeight: c.bold ? 700 : 500, color: '#334155' }}>
                      {c.cell ? c.cell(r, baseIndex + idx + 1) : c.text(r, baseIndex + idx + 1)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={Math.min(page, Math.max(Math.ceil(total / rowsPerPage) - 1, 0))}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid #e2e8f0' }}
        />
      </Paper>

      <Dialog open={notesOpen} onClose={() => setNotesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, fontFamily: FONT }}>Note</DialogTitle>
        <DialogContent dividers>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            {(data?.notes || []).map((n, i) => (
              <Typography component="li" key={i} variant="body2" sx={{ mb: 1, fontFamily: FONT }}>{n}</Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SellerSalesReport;
